// lodPartMerge
//
// A fragment GLB carries three LOD meshes — fine, medium, coarse — and
// each of those may hold SEVERAL primitives. A door is the clearest
// case: door panel, frame and glazing are three primitives of the same
// mesh because they carry three different materials. glTF has no other
// way to express "one mesh, several materials".
//
// three.js's GLTFLoader expands a multi-primitive mesh into a Group of
// Meshes — one child per primitive. So a 3-LOD x 3-primitive door does
// not arrive as 3 scene meshes, it arrives as 9. Both parse paths used
// to walk the scene and take the first three `isMesh` nodes as
// fine/medium/coarse, which for that door meant the three primitives of
// the FINE mesh: the panel was rendered as "fine", the FRAME became
// "medium" and the glass "coarse". Only ever one part visible at a
// time, and at close range that part is the panel — doors rendered
// without their framing. Measured against a real export: 12 of 354
// fragments and 8.9% of all triangles never reached the screen, across
// Doors, Windows, Structural Framing, Generic Models, Floors, Roofs and
// Columns.
//
// The fix is in two halves. `bucketMeshesByLod` restores the LOD
// grouping the GLB actually describes, and `mergeLodParts` collapses
// each bucket back into ONE geometry so the rest of the pipeline is
// untouched: GlobalInstancePool still builds a single InstancedMesh per
// (hash, lod), and InstanceStateApplicator still drives one instance
// per element via matrix + instanceColor.
//
// Multi-material survives via geometry groups + a material ARRAY, which
// three renders as one draw call per group. That is safe here because
// nothing downstream treats a slot material as a single object:
// GeometryCache disposes geometry only ("materials are shared with
// three's cache"), the applicator never touches .material at all, and
// both Mesh.raycast and WebGLRenderer.projectObject already handle
// material arrays by iterating geometry.groups.

import { BufferGeometry, BufferAttribute } from 'three';
import { mergeGeometries as mergeBufferGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils';

/**
 * Which LOD a mesh node belongs to.
 *
 * The writer names its three meshes "fine" / "medium" / "coarse". When a
 * mesh has several primitives GLTFLoader splits it into that many Mesh
 * children and uniquifies their names, so they arrive as "fine",
 * "fine_1", "fine_2" — the same LOD, distinguishable by stripping the
 * numeric suffix.
 *
 * This used to key on the parent Group instead, on the assumption that
 * GLTFLoader creates one Group per multi-primitive mesh. That holds for
 * a door (three Groups of three primitives) but NOT for a fragment
 * whose three single-primitive LOD meshes hang off one shared parent —
 * there, every LOD keyed to the same Group, all three were merged into
 * a single slot, and the remaining two slots were filled with that same
 * geometry OBJECT. The first LOD switch then disposed the geometry the
 * other slots were still pointing at, and the element stopped drawing
 * entirely. Names separate the two layouts correctly; parent identity
 * does not.
 *
 * Falls back to parent/self identity for an unnamed mesh, which is the
 * old behaviour and still right when there is nothing better to go on.
 */
function lodKeyOf(node) {
  const name = String(node.name || '');
  if (name) return name.replace(/_\d+$/, '');
  return (node.parent && node.parent.isGroup) ? node.parent.uuid : node.uuid;
}

/**
 * Group the meshes of a parsed fragment scene by the LOD they came from.
 *
 * Insertion order is document order, which the writer emits as
 * fine, medium, coarse.
 *
 * @param {THREE.Object3D} scene  gltf.scene
 * @param {number} maxLods        stop after this many buckets
 * @returns {Array<Array<THREE.Mesh>>} one array of primitives per LOD
 */
export function bucketMeshesByLod(scene, maxLods = 3) {
  const byKey = new Map();
  scene.traverse((node) => {
    if (!node.isMesh) return;
    const key = lodKeyOf(node);
    let bucket = byKey.get(key);
    if (!bucket) {
      // A new LOD. Stop collecting once we have the LODs we can use —
      // but keep filling buckets already open, or a fragment whose
      // fine mesh has four primitives would lose the fourth.
      if (byKey.size >= maxLods) return;
      bucket = [];
      byKey.set(key, bucket);
    }
    bucket.push(node);
  });
  return Array.from(byKey.values());
}

/**
 * Collapse one LOD's primitives into a single { geometry, material, name }
 * slot.
 *
 * Single-primitive LODs — 341 of the 354 fragments in a real export —
 * return their part untouched. They are the overwhelming majority and
 * they were never broken; this keeps them on exactly the code path they
 * were on before.
 *
 * @param {Array<{geometry, material, name}>} parts
 * @returns {{geometry, material, name}|null}
 */
export function mergeLodParts(parts) {
  const usable = (parts || []).filter(p => p && p.geometry);
  if (usable.length === 0) return null;
  if (usable.length === 1) return usable[0];

  // mergeBufferGeometries requires every input to carry the SAME
  // attribute set and to agree on being indexed; it returns null (after
  // logging) when they don't. Trim each geometry to the attributes
  // common to all of them rather than letting one primitive's stray
  // tangent/uv2 fail the whole merge — a missing uv on a fragment we
  // render untextured costs nothing, a dropped door frame is the bug
  // we're here to fix.
  let common = null;
  for (const p of usable) {
    const names = new Set(Object.keys(p.geometry.attributes));
    common = common === null ? names : new Set([...common].filter(n => names.has(n)));
  }
  if (!common || common.size === 0 || !common.has('position')) {
    return _fallback(usable, 'no common position attribute');
  }

  const indexed = usable.map(p => p.geometry.index !== null);
  if (indexed.some(Boolean) && !indexed.every(Boolean)) {
    return _fallback(usable, 'mixed indexed/non-indexed primitives');
  }

  const trimmed = usable.map(p => _normalize(p.geometry, common));

  // useGroups: true — one group per primitive, each pointing at its own
  // index in the material array below.
  const merged = mergeBufferGeometries(trimmed, true);
  if (!merged) return _fallback(usable, 'mergeBufferGeometries returned null');

  return {
    geometry: merged,
    material: usable.map(p => p.material),
    name: usable[0].name || '',
  };
}

/**
 * A geometry carrying exactly `common`, with every attribute a plain
 * (de-interleaved) BufferAttribute.
 *
 * Both are required by mergeBufferGeometries: it refuses a set whose
 * attribute names differ, and it cannot read InterleavedBufferAttribute
 * at all ("InterleavedBufferAttributes are not supported"). Our
 * fragment GLBs ARE interleaved — position/normal/uv share one buffer —
 * so without this the merge fails on every real door and falls back to
 * the single-primitive behaviour we're trying to remove.
 *
 * De-interleaved by hand rather than via InterleavedBufferAttribute
 * .clone(): that method console.logs on every call (once per attribute
 * per primitive, on every fragment parse) and accumulates through a
 * boxed JS array before copying into a typed one.
 *
 * The input geometry is never mutated — it may be a cached original
 * handed to us again on a later parse.
 */
function _normalize(g, common) {
  const needsWork =
    Object.keys(g.attributes).some(n => !common.has(n)) ||
    [...common].some(n => g.attributes[n].isInterleavedBufferAttribute);
  if (!needsWork) return g;

  const out = new BufferGeometry();
  // Index is shared, not copied: mergeBufferGeometries only reads it.
  if (g.index) out.setIndex(g.index);

  for (const name of common) {
    const a = g.attributes[name];
    if (!a.isInterleavedBufferAttribute) {
      out.setAttribute(name, a);
      continue;
    }
    const { itemSize, offset, normalized } = a;
    const stride = a.data.stride;
    const src = a.data.array;
    const dst = new src.constructor(a.count * itemSize);
    for (let i = 0; i < a.count; i++) {
      const from = i * stride + offset;
      const to = i * itemSize;
      for (let j = 0; j < itemSize; j++) dst[to + j] = src[from + j];
    }
    out.setAttribute(name, new BufferAttribute(dst, itemSize, normalized));
  }
  return out;
}

// Degrade to the pre-fix behaviour (first primitive only) rather than
// dropping the fragment entirely — a partially-drawn element beats an
// invisible one — but say so, because it means a fragment is still
// losing geometry and the writer produced something we didn't expect.
function _fallback(usable, why) {
  console.warn(
    `[lodPartMerge] cannot merge ${usable.length} primitives (${why}); ` +
    'rendering the first only — this element will be missing parts.');
  return usable[0];
}
